import type {
	AnalyticsControllerGetComponentRuleStatisticsPathParams,
	AnalyticsControllerGetPageRuleStatisticsError,
	AnalyticsControllerGetPageRuleStatisticsPathParams,
	AnalyticsControllerGetRunRuleStatisticsPathParams,
} from '@/api/repository/repositoryComponents'
import type { RepositoryContext } from '@/api/repository/repositoryContext'
import { useRepositoryContext } from '@/api/repository/repositoryContext'
import { repositoryFetch } from '@/api/repository/repositoryFetcher'
import type {
	AnalyticsRequestDTO,
	AnalyticsResultsDTO,
} from '@/api/repository/repositorySchemas'
import * as reactQuery from '@tanstack/react-query'
import qs from 'qs'

import type * as Fetcher from '../../api/repository/repositoryFetcher'

type Variables = {
	pathParams:
		| AnalyticsControllerGetPageRuleStatisticsPathParams
		| AnalyticsControllerGetComponentRuleStatisticsPathParams
		| AnalyticsControllerGetRunRuleStatisticsPathParams
	queryParams?: AnalyticsRequestDTO & { forceRebuild?: boolean }
} & RepositoryContext['fetcherOptions']

type Path =
	| '/api/v2/analytics/app/{appId}/pages'
	| '/api/v2/analytics/app/{appId}/components'
	| '/api/v2/analytics/app/{appId}/run/{runId}'

const resolvePathParam = (key: string, pathParams: Record<string, string>) => {
	if (key.startsWith('{') && key.endsWith('}')) {
		return pathParams[key.slice(1, -1)]
	}
	return key
}

export const queryKeyFn = (operation: { path: Path; variables: Variables }) => {
	const queryKey: unknown[] = operation.path
		.split('/')
		.filter(Boolean)
		.map(i => resolvePathParam(i, operation.variables.pathParams))

	queryKey.push(operation.variables.queryParams)

	return queryKey
}

export const fetchAnalytics = (
	path: Path,
	variables: Variables,
	signal?: AbortSignal
) =>
	repositoryFetch<
		AnalyticsResultsDTO,
		Fetcher.ErrorWrapper<undefined>,
		undefined,
		{},
		Variables['queryParams'],
		Variables['pathParams']
	>({
		url: path,
		method: 'get',
		...variables,
		signal,
	})

export const useAnalytics = <TData = AnalyticsResultsDTO>(
	path: Path,
	variables: Variables,
	options?: Omit<
		reactQuery.UseQueryOptions<
			AnalyticsResultsDTO,
			AnalyticsControllerGetPageRuleStatisticsError,
			TData
		>,
		'queryKey' | 'queryFn' | 'initialData'
	>
) => {
	const { fetcherOptions, queryOptions } = useRepositoryContext(options)

	const formattedVariables =
		variables &&
		'queryParams' in variables &&
		'filter' in variables.queryParams
			? {
					...variables,
					pathParams: { ...variables.pathParams },
					queryParams: qs.stringify(variables.queryParams, {
						skipNulls: true,
					}),
				}
			: fetcherOptions

	return reactQuery.useQuery<
		AnalyticsResultsDTO,
		AnalyticsControllerGetPageRuleStatisticsError,
		TData
	>({
		queryKey: queryKeyFn({
			path,
			variables,
		}),
		queryFn: ({ signal }) =>
			fetchAnalytics(
				path,
				{ ...fetcherOptions, ...formattedVariables } as Variables,
				signal
			),
		...options,
		...queryOptions,
	})
}
